import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import CommonModal from "../shared/modalLayout/CommonModal";
import CustomDropdown from "../shared/dropdown/CustomDropdown";
import HospitalCoveragePicker from "./HospitalCoveragePicker";
import PasswordInput, { isValidLoginPassword } from "./PasswordInput";
import {
  useCreateVaDistributorMutation,
  useUpdateVaDistributorMutation,
  useGetVaAuth0UsersQuery,
} from "../../app/vaApi/vaApi";
import { useVaCommissions } from "../../hooks/useVaCommissions";
import { useVaHospitals } from "../../hooks/useVaHospitals";
import { showToast } from "../../helpers/Utils";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  auth0UserId: "",
  password: "",
  confirmPassword: "",
  hospitalIds: [],
  commissionPercent: "",
};

const VA_NAME_MAX_LENGTH = 50;

export default function AddVaDistributorModal({
  show,
  setShow,
  editItem,
  onSuccess,
  onCreated,
}) {
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const { commissions } = useVaCommissions();
  const { stateGroups, isLoading: loadingHospitals } = useVaHospitals({
    skip: !show,
  });
  const { data: auth0UsersData, isFetching: loadingUsers } =
    useGetVaAuth0UsersQuery(
      { page: 1, limit: 100, role: "va-distributor" },
      { skip: !show || !!editItem }
    );
  const [createDistributor, { isLoading: creating }] =
    useCreateVaDistributorMutation();
  const [updateDistributor, { isLoading: updating }] =
    useUpdateVaDistributorMutation();

  const isEdit = !!editItem;
  const isSubmitting = creating || updating;
  const auth0Users = auth0UsersData?.data ?? [];
  const needsPassword = !isEdit && !form.auth0UserId;
  const showPasswordFields = needsPassword || isEdit;

  useEffect(() => {
    if (!show) return;
    if (editItem) {
      setForm({
        name: editItem.name,
        email: editItem.email,
        phone: editItem.phone || "",
        auth0UserId: editItem.auth0UserId || "",
        password: "",
        confirmPassword: "",
        hospitalIds: Array.isArray(editItem.hospitalIds)
          ? editItem.hospitalIds.map(String)
          : [],
        commissionPercent:
          editItem.commissionPercent ??
          editItem.commission?.percent ??
          editItem.commission?.id ??
          "",
      });
    } else {
      setForm(emptyForm);
    }
    setFieldErrors({});
  }, [show, editItem]);

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    else if (form.name.trim().length > VA_NAME_MAX_LENGTH) {
      errors.name = `Name must be ${VA_NAME_MAX_LENGTH} characters or fewer`;
    }
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Enter a valid email";
    }

    const passwordEntered = Boolean(form.password || form.confirmPassword);
    if (needsPassword || passwordEntered) {
      if (!form.password) {
        errors.password = needsPassword
          ? "Password is required for Auth0 login"
          : "Enter a new password or leave both fields blank";
      } else if (!isValidLoginPassword(form.password)) {
        errors.password =
          "Password must be 8–50 characters with upper, lower, number, and special character.";
      }
      if (!form.confirmPassword) {
        errors.confirmPassword = "Confirm password is required";
      } else if (form.password !== form.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
    }

    if (!form.hospitalIds.length) {
      errors.hospitalIds = "Select at least one hospital";
    }
    if (form.commissionPercent === "" || form.commissionPercent == null) {
      errors.commissionPercent = "Commission is required";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      hospitalIds: form.hospitalIds,
      commissionPercent: Number(form.commissionPercent),
    };
    if (!isEdit && form.auth0UserId) {
      payload.auth0UserId = form.auth0UserId;
    }
    if (form.password) {
      payload.password = form.password;
    }

    try {
      if (isEdit) {
        await updateDistributor({ id: editItem.id, ...payload }).unwrap();
        showToast(
          form.password
            ? "Distributor and password updated successfully"
            : "Distributor updated successfully",
          "success"
        );
      } else {
        const result = await createDistributor(payload).unwrap();
        showToast("Distributor created successfully", "success");
        setShow(false);
        const newId = result?.data?.id;
        if (newId && onCreated) {
          onCreated(newId);
          return;
        }
      }
      setShow(false);
      onSuccess?.();
    } catch (err) {
      const msg = err?.data?.message || "Something went wrong";
      const field = err?.data?.field;
      if (field) setFieldErrors((prev) => ({ ...prev, [field]: msg }));
      else showToast(msg, "error");
    }
  };

  return (
    <CommonModal
      show={show}
      setShow={setShow}
      title={isEdit ? "Edit VA Distributor" : "Add New VA Distributor"}
      className="modal-lg add-va-distributor-modal"
      centered
      onConfirm={handleSubmit}
      confirmLoading={isSubmitting}
      confirmDisabled={isSubmitting}
      buttonSize="medium"
    >
      <div className="row g-3 va-distributor-fields">
        {!isEdit && (
          <div className="col-12">
            <div className="va-field">
              <div className="va-field__header">
                <label className="cmn_label mb-0">Existing Auth0 user</label>
                <span className="va-field__hint">Optional</span>
              </div>
              <CustomDropdown
                label={
                  loadingUsers
                    ? "Loading existing users..."
                    : "Select existing user or enter a new one"
                }
                value={form.auth0UserId}
                onSelect={(opt) => {
                  const selectedId = String(opt?.value ?? "").trim();
                  if (!selectedId || selectedId === "__none__") {
                    setForm((prev) => ({
                      ...prev,
                      auth0UserId: "",
                      name: "",
                      email: "",
                      password: "",
                      confirmPassword: "",
                    }));
                    return;
                  }
                  const selected = auth0Users.find(
                    (u) => String(u.auth0UserId) === selectedId
                  );
                  setForm((prev) => ({
                    ...prev,
                    auth0UserId: selectedId,
                    name: String(selected?.name || opt.label || "").slice(
                      0,
                      VA_NAME_MAX_LENGTH
                    ),
                    email: selected?.email || "",
                    password: "",
                    confirmPassword: "",
                  }));
                }}
                options={[
                  {
                    value: "__none__",
                    label: "None — enter new user manually",
                  },
                  ...auth0Users.map((u) => ({
                    value: String(u.auth0UserId),
                    label: u.name || u.email || u.auth0UserId,
                  })),
                ]}
                className="w-100"
                disabled={loadingUsers}
              />
            </div>
          </div>
        )}

        <div className="col-md-6">
          <div className="va-field">
            <div className="va-field__header">
              <label className="cmn_label mb-0">
                Full Name <span className="text-danger">*</span>
              </label>
            </div>
            <Form.Control
              name="name"
              value={form.name}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  name: e.target.value.slice(0, VA_NAME_MAX_LENGTH),
                  auth0UserId: "",
                }))
              }
              placeholder="Enter full name"
              maxLength={VA_NAME_MAX_LENGTH}
              className={`cmn_input va-field__input${
                fieldErrors.name ? " va-field__input--error" : ""
              }`}
            />
            {fieldErrors.name && (
              <p className="va-field__error">{fieldErrors.name}</p>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="va-field">
            <div className="va-field__header">
              <label className="cmn_label mb-0">
                Email Address <span className="text-danger">*</span>
              </label>
            </div>
            <Form.Control
              type="email"
              name="email"
              value={form.email}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  email: e.target.value,
                  auth0UserId: "",
                }))
              }
              placeholder="Enter email address"
              disabled={Boolean(form.auth0UserId)}
              readOnly={Boolean(form.auth0UserId)}
              className={`${
                form.auth0UserId ? "disabled_input" : "cmn_input"
              } va-field__input${
                fieldErrors.email ? " va-field__input--error" : ""
              }`}
            />
            {form.auth0UserId ? (
              <p className="va-distributor-fields__helper mb-0">
                Email comes from the selected Auth0 user.
              </p>
            ) : null}
            {fieldErrors.email && (
              <p className="va-field__error">{fieldErrors.email}</p>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="va-field">
            <div className="va-field__header">
              <label className="cmn_label mb-0">Phone Number</label>
            </div>
            <Form.Control
              type="tel"
              name="phone"
              value={form.phone}
              onChange={(e) =>
                setForm((p) => ({ ...p, phone: e.target.value }))
              }
              placeholder="Enter phone number (optional)"
              className="cmn_input va-field__input"
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="va-field">
            <div className="va-field__header">
              <label className="cmn_label mb-0">
                Commission Rate <span className="text-danger">*</span>
              </label>
            </div>
            <CustomDropdown
              label="Select commission rate"
              value={
                form.commissionPercent === "" || form.commissionPercent == null
                  ? ""
                  : String(form.commissionPercent)
              }
              onSelect={(opt) =>
                setForm((p) => ({ ...p, commissionPercent: opt.value }))
              }
              options={commissions.map((c) => ({
                value: String(c.percent ?? c.id),
                label: c.label,
              }))}
              error={!!fieldErrors.commissionPercent}
              className="w-100"
            />
            {fieldErrors.commissionPercent && (
              <p className="va-field__error">{fieldErrors.commissionPercent}</p>
            )}
          </div>
        </div>

        {showPasswordFields ? (
          <>
            <div className="col-md-6">
              <div className="va-field">
                <div className="va-field__header">
                  <label className="cmn_label mb-0">
                    Password
                    {needsPassword ? (
                      <span className="text-danger"> *</span>
                    ) : null}
                  </label>
                </div>
                <PasswordInput
                  name="password"
                  value={form.password}
                  onChange={(e) => {
                    const password = e.target.value;
                    setForm((p) => ({ ...p, password }));
                    setFieldErrors((prev) => {
                      if (!prev.password) return prev;
                      const next = { ...prev };
                      delete next.password;
                      return next;
                    });
                  }}
                  placeholder={
                    isEdit
                      ? "Enter new password (optional)"
                      : "Create login password"
                  }
                  error={
                    !!fieldErrors.password ||
                    (Boolean(form.password) &&
                      !isValidLoginPassword(form.password))
                  }
                />
                {(fieldErrors.password ||
                  (form.password &&
                    !isValidLoginPassword(form.password))) && (
                  <p className="va-field__error">
                    {fieldErrors.password ||
                      "Password must be 8–50 characters with upper, lower, number, and special character."}
                  </p>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="va-field">
                <div className="va-field__header">
                  <label className="cmn_label mb-0">
                    Confirm Password
                    {needsPassword || form.password ? (
                      <span className="text-danger"> *</span>
                    ) : null}
                  </label>
                </div>
                <PasswordInput
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      confirmPassword: e.target.value,
                    }))
                  }
                  placeholder={
                    isEdit ? "Re-enter new password" : "Re-enter password"
                  }
                  error={fieldErrors.confirmPassword}
                />
                {fieldErrors.confirmPassword && (
                  <p className="va-field__error">
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : null}

        <div className="col-12">
          <HospitalCoveragePicker
            hospitalIds={form.hospitalIds}
            onChange={(hospitalIds) =>
              setForm((p) => ({ ...p, hospitalIds }))
            }
            error={fieldErrors.hospitalIds}
            stateGroups={stateGroups}
            isLoading={loadingHospitals}
            currentDistributorId={editItem?.id || editItem?._id || null}
            hideAssignedTab={!isEdit}
          />
        </div>
      </div>
    </CommonModal>
  );
}
